// Rooms: one event streamed from several places at once (stages, tracks,
// courts...). The first event is the primary — its link is the one guests
// get and it owns the guest list. Each extra room is its own event (stream
// key, recording, chat/guestbook; one credit per day), linked with room_of.
// Server side: eventlive-cdk/lambda/rooms.ts.
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";

import { t, tn } from "@/localization";
import { roomLabelText } from "./roomLabels";
import {
  MAX_CAMERAS,
  MULTICAM_B2C,
  addsCameras,
  isCameraGroup,
} from "./multicam";

// The nouns are values (room_noun on the server, compared below); people read
// them as AddRoomPane's noun picker shows them. The labels the server gives
// rooms ("Stage 2", and "Stage 1" for the primary) are saved in English;
// roomLabelText (roomLabels.js) shows them in the app's language.
export const ROOM_NOUNS = [
  "Room",
  "Stage",
  "Track",
  "Court",
  "Field",
  "Hall",
  "Camera",
];
export const DEFAULT_ROOM_NOUN = "Room";
// Extra rooms per event (the primary is not counted), as on the server.
export const MAX_ROOMS = 9;

export const roomSlugsOf = (event) => {
  const raw = event?.room_slugs;
  return Array.isArray(raw)
    ? raw.filter((s) => typeof s === "string" && s)
    : [];
};

export const isRoom = (event) => !!event?.room_of;
export const hasRooms = (event) => roomSlugsOf(event).length > 0;
export const isGrouped = (event) => isRoom(event) || hasRooms(event);
export const primarySlugOf = (event) => event?.room_of || event?.slug;

export const roomNounOf = (event) =>
  ROOM_NOUNS.find((n) => n === event?.room_noun) || DEFAULT_ROOM_NOUN;

export { roomLabelText };

// "Track 1" for a primary whose first room is named after the noun.
export const roomLabelOf = (event) =>
  roomLabelText(
    event?.room_label ||
      (event?.room_of ? DEFAULT_ROOM_NOUN : `${roomNounOf(event)} 1`)
  );

// Cameras film one event from more angles, so one ticket always covers them
// all, on personal and business accounts alike. Other rooms can sell their own.
export const ticketingModeOf = (primary) =>
  primary?.rooms_ticketing === "per_room" && !isCameraGroup(primary)
    ? "per_room"
    : "shared";

// Mirrors the server's isTicketed: does this event sell tickets itself?
export const isTicketed = (event) => {
  if (!event?.stripe_connected_account_id) return false;
  const mode = event.pricing_mode;
  if (mode === "custom_price") return !!event.custom_price?.length;
  if (mode === "customer_chooses")
    return !!event.customer_chooses_price_default?.length;
  return !!(mode || event.stripe_price_id || event.customer_chooses_price);
};

// Whose tickets/guest list a room uses: the primary's unless the primary
// sells a ticket per room and this room has its own ticket settings.
export const usesPrimaryAudience = (event, primary) =>
  !!event?.room_of &&
  !!primary &&
  !(ticketingModeOf(primary) === "per_room" && isTicketed(event));

export const subscriptionActive = (auth) =>
  !!auth?.subscription_access_expiry_datetime &&
  auth.subscription_access_expiry_datetime > moment().utc().format();

// Rooms are a business feature: credits and the Unlimited plan pay for them.
// Personal accounts get them as cameras (multicam.js), up to MAX_CAMERAS.
export const canAddRoom = (
  event,
  auth,
  roomCount = 0,
  multicam = MULTICAM_B2C
) =>
  !!event &&
  (auth?.account_type === "business" || addsCameras(auth, multicam)) &&
  !event.is_test &&
  !event.deleted &&
  !event.archived &&
  event.event_status !== "past" &&
  roomCount < (auth?.account_type === "business" ? MAX_ROOMS : MAX_CAMERAS - 1);

// What adding one room costs this account, in plain words.
export const roomCostText = (primary, auth) => {
  const days = Math.max(1, Number(primary?.days) || 1);
  if (subscriptionActive(auth)) {
    return t("Included in your Unlimited plan.");
  }
  const credits = Number(auth?.credits) || 0;
  const cost =
    days > 1
      ? t(
          "Each room uses 1 credit per day: {days} credits for this {days}-day event.",
          { days }
        )
      : t("Each room uses 1 credit.");
  if (credits >= days) {
    return `${cost} ${tn(
      "You have {count} credit.",
      "You have {count} credits.",
      credits
    )}`;
  }
  return `${cost} ${
    days === 1
      ? t("You'll be asked to get a credit after adding it.")
      : t("You'll be asked to get credits after adding it.")
  }`;
};

const byRoomIndex = (a, b) => (a?.room_index || 0) - (b?.room_index || 0);

// Dashboard: rooms are listed under their primary instead of as their own
// cards. A room whose primary isn't loaded stays a card of its own.
export const groupEventsWithRooms = (events = []) => {
  const bySlug = new Map(events.map((e) => [e.slug, e]));
  const roomsOf = new Map();
  for (const event of events) {
    if (event.room_of && bySlug.has(event.room_of)) {
      if (!roomsOf.has(event.room_of)) roomsOf.set(event.room_of, []);
      roomsOf.get(event.room_of).push(event);
    }
  }
  return events
    .filter((event) => !(event.room_of && bySlug.has(event.room_of)))
    .map((event) =>
      roomsOf.has(event.slug)
        ? { ...event, rooms: roomsOf.get(event.slug).sort(byRoomIndex) }
        : event
    );
};

// Search on the dashboard also finds a group through its rooms' names.
export const matchesSearch = (event, search) => {
  if (!search) return true;
  const needle = search.toLowerCase();
  return [event, ...(event.rooms || [])].some(
    (e) =>
      (e.title || "").toLowerCase().includes(needle) ||
      (e.room_label || "").toLowerCase().includes(needle)
  );
};

// The group as one ordered list: the primary first, then its rooms.
export const groupList = (primary, rooms = []) =>
  primary
    ? [primary, ...[...rooms].sort(byRoomIndex)]
    : [...rooms].sort(byRoomIndex);

// Sentences that name the noun are written out for each one, so a translation
// can agree with it ("una sala nueva", "un escenario nuevo"): each component
// keeps a table { Room: () => …, Stage: () => …, … } of its own sentences
// (AddRoomPane, RoomMenu, RoomPickerModal, RoomSwitcher, useAddRoom,
// useRoomsTicketing). This picks `noun`'s entry; anything else reads as a room.
export const forNoun = (table, noun) =>
  table[ROOM_NOUNS.includes(noun) ? noun : DEFAULT_ROOM_NOUN]();
